import Button, { buildHidePopoverIfAlreadyShowing, ButtonWithPopover, defaultAnimationWithHidingSmooth } from "Components/button";
import TreeView from "devextreme-react/tree-view";
import filter from "lodash/filter";
import flatMap from "lodash/flatMap";
import forEach from "lodash/forEach";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import join from "lodash/join";
import map from "lodash/map";
import some from "lodash/some";
import startsWith from "lodash/startsWith";
import uniq from "lodash/uniq";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import locale from "../taskStatisticsFilter.locale";
import Styled from "./projectsFilter.styled";
import { buildTreeNodes } from "./utils/buildTreeNodes";
import TogglerWithFloatLabel from "Components/scrolledDropdown/togglerWithFloatLabel/togglerWithFloatLabel";
const menuPosition = {
    my: "top left",
    at: "bottom left",
    collision: "fit flip",
    offset: { y: -1 }
};
const SIZES = {
    BUTTON_HEIGHT: 24,
    POPOVER_MIN_HEIGHT: 36,
    POPOVER_MARGIN: 0.5
};
function ProjectsFilter({ projectIds, selectedWorkSpaceId, workSpaces, width, height, disabled, caption, popoverWidth, popoverHeight, withApplyButton, className, isCleared, onApplyFilter }) {
    const [open, setOpen] = useState(false);
    const [selectedProjectIds, setSelectedProjectIds] = useState([]);
    const [tree, setTree] = useState();
    const popoverRef = useRef(null);
    const id = useMemo(() => `id${uuid()}`, []);
    const togglerId = `toggler_${id}`;
    const handleHideIfAlreadyShowing = useCallback(buildHidePopoverIfAlreadyShowing(popoverRef), []);
    const filteredWorkSpaces = useMemo(() => {
        if (selectedWorkSpaceId) {
            return filter(workSpaces, ["Id", selectedWorkSpaceId]);
        }
        return workSpaces;
    }, [workSpaces, selectedWorkSpaceId]);
    const buildNodeTree = useCallback((ids) => {
        filteredWorkSpaces && setTree(buildTreeNodes(filteredWorkSpaces, ids, !!selectedWorkSpaceId));
    }, [selectedWorkSpaceId, filteredWorkSpaces]);
    const setSelectedProjectIdsAndUpdateTree = (ids) => {
        buildNodeTree(ids);
        setSelectedProjectIds(ids);
    };
    useEffect(() => {
        if (!projectIds) {
            setSelectedProjectIdsAndUpdateTree([]);
        }
        else {
            setSelectedProjectIdsAndUpdateTree(projectIds);
        }
    }, [projectIds, workSpaces, isCleared]);
    useEffect(() => {
        var _a;
        if (selectedWorkSpaceId && filteredWorkSpaces) {
            const projectIdsForSelectedWorkSpace = map((_a = filteredWorkSpaces[0]) === null || _a === void 0 ? void 0 : _a.Projects, "Id");
            setSelectedProjectIdsAndUpdateTree(projectIdsForSelectedWorkSpace);
        }
    }, [selectedWorkSpaceId]);
    const getTitle = useMemo(() => {
        const projects = flatMap(workSpaces, (workSpace) => workSpace.Projects && workSpace.Projects);
        if (isEmpty(selectedProjectIds))
            return undefined;
        const selectedProjects = filter(projects, item => includes(selectedProjectIds, item && item.Id));
        const selectedWorkSpaces = !selectedWorkSpaceId
            ? filter(workSpaces, (workspace) => some(map(workspace.Projects, "Id"), projectId => includes(selectedProjectIds, projectId)))
            : [];
        const names = map([...selectedWorkSpaces, ...selectedProjects], p => (p ? p.Name : ""));
        if (isEmpty(selectedProjects))
            return "";
        return join(names, ", ");
    }, [workSpaces, selectedProjectIds, selectedWorkSpaceId]);
    const handleDropdownToggleClick = useCallback(() => {
        setOpen(!open);
    }, [open]);
    const handleDropDownClose = useCallback(() => {
        setOpen(false);
    }, []);
    const handleProjectSelected = useCallback((treeItem) => {
        if (treeItem.selected) {
            if (!includes(selectedProjectIds, Number(treeItem.id))) {
                setSelectedProjectIds(prev => [...prev, Number(treeItem.id)]);
            }
        }
        else {
            setSelectedProjectIds(prev => filter(prev, projectId => projectId !== Number(treeItem.id)));
        }
    }, [workSpaces, selectedProjectIds]);
    const handleItemSelected = useCallback((element) => {
        if (startsWith(element.itemData.id, "workSpace")) {
            forEach(element.itemData.items, handleProjectSelected);
        }
        else {
            handleProjectSelected(element.itemData);
        }
    }, [handleProjectSelected]);
    const handleApplyClick = useCallback(() => {
        var _a;
        const uniqueIds = uniq(selectedProjectIds);
        onApplyFilter({
            projectId: uniqueIds.length ? map(uniqueIds, projectId => Number(projectId)) : undefined
        });
        (_a = popoverRef.current) === null || _a === void 0 ? void 0 : _a.instance.hide();
    }, [onApplyFilter, projectIds, selectedProjectIds]);
    const handleCancelClick = useCallback(() => {
        var _a;
        setSelectedProjectIdsAndUpdateTree(projectIds || []);
        (_a = popoverRef.current) === null || _a === void 0 ? void 0 : _a.instance.hide();
    }, [projectIds, filteredWorkSpaces, onApplyFilter, selectedProjectIds, selectedWorkSpaceId]);
    const toggleProps = {
        disabled,
        label: caption,
        caption: "",
        maxLength: 100,
        height: 44,
        className: "normal-wrap text-left w-100 pl-xs pr-xs",
        muteDefaultCaptionText: true,
        selectedKeyCaption: getTitle,
        onClick: handleDropdownToggleClick,
        id: togglerId,
        open
    };
    const style = useMemo(() => ({
        display: "inline-block",
        verticalAlign: "middle",
        height: height !== null && height !== void 0 ? height : SIZES.BUTTON_HEIGHT,
        width
    }), [width]);
    const menu = useMemo(() => tree && (React.createElement(TreeView, { searchEnabled: true, selectNodesRecursive: true, showCheckBoxesMode: "normal", selectionMode: "multiple", items: tree, width: "100%", height: `calc(100% - ${SIZES.POPOVER_MARGIN}em)`, onItemSelectionChanged: handleItemSelected, searchEditorOptions: { stylingMode: "filled" } })), [tree, handleItemSelected]);
    const popoverContent = useMemo(() => (React.createElement("div", { className: "grid-rows-1fr-auto h-100" },
        React.createElement(Styled.MenuWrapper, { className: "popover-content", marginSize: SIZES.POPOVER_MARGIN }, menu),
        menu && withApplyButton && (React.createElement("div", { className: "caption-with-actions-panel h-center-content with-gap-sm pl-0_25 pb-0_75 pr-0_25" },
            React.createElement(Button, { onClick: handleCancelClick, className: "btn btn-default mt-0_75", height: 36 }, locale.CANCEL_FILTER),
            React.createElement(Button, { onClick: handleApplyClick, className: "btn btn-primary mt-0_75", disabled: !selectedProjectIds.length && !!selectedWorkSpaceId, height: 36 }, locale.APPLY_FILTER))))), [menu, withApplyButton, handleApplyClick, handleCancelClick]);
    return (React.createElement("div", { title: getTitle, style: style, className: className },
        React.createElement(ButtonWithPopover, { className: "no-border font-small bg-transparent w-100 p-0", showEvent: "dxclick", animation: defaultAnimationWithHidingSmooth, position: menuPosition, popoverRef: popoverRef, onHiding: handleDropDownClose, onClick: handleHideIfAlreadyShowing, ignoreHidingWhenChanged: true, popoverContent: popoverContent, popoverHeight: menu ? popoverHeight : SIZES.POPOVER_MIN_HEIGHT, popoverWidth: popoverWidth, popoverClassName: "p-0", disabled: disabled },
            React.createElement("div", { className: "dropdown-toggle-flex-space-between w-100" },
                React.createElement(TogglerWithFloatLabel, { ...toggleProps })))));
}
export default memo(ProjectsFilter);
ProjectsFilter.defaultProps = {
    popoverHeight: 350,
    height: 200,
    maxHeight: 200
};
